import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, Trash2, Edit, Download } from 'lucide-react';
import { getReports, deleteReport } from '../../services/expenseService';
import api from '../../services/api';
import { useTabParam } from '../../hooks/useTabParam';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';

const ExpenseReportList = () => {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const latest = useRef(0); // the newest request: a slower, older answer must not replace it (e.g. My Reports arriving after Pending Approvals)

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const canApprove = ['MANAGER', 'HR', 'ADMIN', 'FINANCE'].includes(user.role);
    // 'my' or 'approvals', in the address (?tab=approvals) so Home can open the approvals directly.
    const [activeTab, setActiveTab] = useTabParam(canApprove ? ['my', 'approvals'] : ['my'], 'my');

    const [baseCurrency, setBaseCurrency] = useState('INR');

    useEffect(() => {
        const fetchBaseCurrency = async () => {
            try {
                const res = await api.get('/auth/config');
                setBaseCurrency(res.data.baseCurrency || 'INR');
            } catch (err) {
                console.error('Failed to fetch base currency', err);
            }
        };
        fetchBaseCurrency();
        loadReports(activeTab);
    }, [activeTab]);

    const loadReports = async (type) => {
        const mine = ++latest.current;
        setLoading(true);
        const data = await getReports(type);
        if (mine !== latest.current) return; // a newer request was made while this one was on its way
        // Sort DESC
        const sorted = (data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setReports(sorted);
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this report?')) {
            const ok = await deleteReport(id);
            if (!ok) alert('The report could not be deleted. It may have been submitted or changed; reload and try again.');
            loadReports(activeTab);
        }
    };

    const StatusBadge = ({ status }) => {
        const colors = {
            DRAFT: '#6b7280',
            SUBMITTED: '#eab308',
            PENDING_FINANCE: '#3b82f6',
            APPROVED: '#22c55e',
            REJECTED: '#ef4444'
        };
        return (
            <span style={{
                padding: '0.2rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: colors[status] || '#6b7280',
                color: 'white',
                marginLeft: '0.5rem'
            }}>
                {status?.replace('_', ' ')}
            </span>
        );
    };

    return (
        <div className="container section">
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <PageHeader crumbs={crumbsFor(activeTab === 'approvals' ? 'expenseApprovals' : 'expenses')} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                    <h1 className="gradient-text" style={{ fontSize: '2.5rem', margin: 0 }}>
                        Expense Reports
                    </h1>
                    <Link to="/tools/expenses/new" className="btn btn-primary">
                        <Plus size={20} style={{ marginRight: '0.5rem' }} />
                        New Report
                    </Link>
                </div>

                {canApprove && (
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '1px solid var(--border)' }}>
                        <button
                            onClick={() => setActiveTab('my')}
                            style={{
                                padding: '1rem 2rem', border: 'none', background: 'none', cursor: 'pointer',
                                borderBottom: activeTab === 'my' ? '2px solid var(--primary)' : 'none',
                                color: activeTab === 'my' ? 'var(--primary)' : 'var(--text-muted)',
                                fontWeight: activeTab === 'my' ? 600 : 400
                            }}
                        >
                            My Reports
                        </button>
                        <button
                            onClick={() => setActiveTab('approvals')}
                            style={{
                                padding: '1rem 2rem', border: 'none', background: 'none', cursor: 'pointer',
                                borderBottom: activeTab === 'approvals' ? '2px solid var(--primary)' : 'none',
                                color: activeTab === 'approvals' ? 'var(--primary)' : 'var(--text-muted)',
                                fontWeight: activeTab === 'approvals' ? 600 : 400
                            }}
                        >
                            Pending Approvals
                        </button>
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '4rem' }}>
                        <p>Loading expenses...</p>
                    </div>
                ) : reports.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '4rem 2rem',
                        background: 'var(--surface)',
                        borderRadius: 'var(--radius)',
                        boxShadow: 'var(--shadow)'
                    }}>
                        <FileText size={64} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
                        <h3 style={{ marginBottom: '0.5rem' }}>
                            {activeTab === 'my' ? 'No expense reports yet' : 'No pending approvals'}
                        </h3>
                        {activeTab === 'my' && (
                            <>
                                <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                                    Create your first expense report to get started
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {reports.map(report => (
                            <div
                                key={report.id}
                                style={{
                                    background: 'var(--surface)',
                                    borderRadius: 'var(--radius)',
                                    boxShadow: 'var(--shadow)',
                                    padding: '1.5rem',
                                    transition: 'transform 0.2s',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                onClick={() => navigate(`/tools/expenses/${report.id}`, { state: { from: activeTab } })}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                                                {report.title || 'Untitled Report'}
                                            </h3>
                                            <StatusBadge status={report.status} />
                                    {report.importActive && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', background: '#e0f2fe', color: '#075985' }}>Reading receipts…</span>}
                                    {report.needsInputCount > 0 && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', background: '#fee2e2', color: '#991b1b' }}>{report.needsInputCount} need{report.needsInputCount === 1 ? 's' : ''} your input</span>}
                                        </div>

                                        {activeTab === 'approvals' && (
                                            <p style={{ fontSize: '0.9rem', color: 'var(--primary)', marginBottom: '0.75rem', fontWeight: 600 }}>
                                                Submitted by: {report.user?.name} ({report.user?.employeeId || 'N/A'})
                                            </p>
                                        )}

                                        {report.purpose && (
                                            <p style={{ color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                                                {report.purpose}
                                            </p>
                                        )}
                                        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                            <span>
                                                📅 {report.startDate ? new Date(report.startDate).toLocaleDateString() : 'N/A'}
                                                {report.endDate && ` to ${new Date(report.endDate).toLocaleDateString()}`}
                                            </span>
                                            <span>
                                                📄 {report.items?.length || 0} expense{report.items?.length !== 1 ? 's' : ''}
                                            </span>
                                            <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
                                                💰 {report.currency || baseCurrency} {parseFloat(report.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <Link
                                            to={`/tools/expenses/${report.id}`}
                                            className="btn btn-secondary"
                                            style={{ padding: '0.5rem 1rem' }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {activeTab === 'my' ? (
                                                <><Edit size={16} style={{ marginRight: '0.5rem' }} /> Edit</>
                                            ) : (
                                                'Review'
                                            )}
                                        </Link>
                                        {activeTab === 'my' && (report.status === 'DRAFT' || report.status === 'REJECTED') && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDelete(report.id); }}
                                                className="btn btn-secondary"
                                                style={{ padding: '0.5rem 1rem', color: '#dc2626' }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExpenseReportList;
